import { NetworkType, getSupportedNetworkTypeFromChainId } from 'abis/set/sets'
import Big from 'big.js'
import type { WithInitialValue } from 'components/widgets/invest-component/interfaces/common'
import { type CurrencyMetadata, type ProductMetadata, productAssetsFactory } from 'indices-list'
import { type PrimitiveAtom, atom } from 'jotai'
import type { atomsWithQuery } from 'jotai-tanstack-query'
import { type Address, zeroAddress } from 'viem'
import { formatWeiToAmount } from 'utils'
import { RPCCallFailedError } from 'utils/errors'
import { defaultCurrency } from '../zero-ex-requests/quotes/model'
import type { PriceStorage } from '../zero-ex-requests/output/model'
import { arbitrum, avalanche, base, bsc, mainnet } from 'viem/chains'
import { z } from 'zod'
import { CAI } from 'products'

export type PriceResponse = {
  chainId: NetworkType
  currency: CurrencyMetadata
  price: Big
}

export type PriceRequest = {
  sellCurrency: CurrencyMetadata
  metadata: ProductMetadata
  chainId: NetworkType
}

export const defaultPriceRequest: PriceRequest = {
  sellCurrency: defaultCurrency,
  chainId: getSupportedNetworkTypeFromChainId(NetworkType.Avalanche),
  metadata: productAssetsFactory(CAI),
}

type QueryPriceReturn = ReturnType<typeof atomsWithQuery<PriceResponse>>
export type QueryPriceRequest = QueryPriceReturn['0']
export type QueryPriceDispatch = QueryPriceReturn['1']

export type PriceRequestAtoms = {
  params: PrimitiveAtom<PriceRequest> & WithInitialValue<PriceRequest>
  response: QueryPriceRequest
  dispatch: QueryPriceDispatch
}

type LatestPriceRPCResponse =
  | {
      error: Error
      result?: undefined
      status: 'failure'
    }
  | {
      error?: undefined
      result: bigint
      status: 'success'
    }

export type Uint8RPCResponse =
  | {
      error: Error
      result?: undefined
      status: 'failure'
    }
  | {
      error?: undefined
      result: number
      status: 'success'
    }

export type BalanceOfRPCResponse =
  | {
      error: Error
      result?: undefined
      status: 'failure'
    }
  | {
      error?: undefined
      result: bigint
      status: 'success'
    }

export type Uint256RPCResponse =
  | {
      error: Error
      result?: undefined
      status: 'failure'
    }
  | {
      error?: undefined
      result: bigint
      status: 'success'
    }

export const priceFeedsContracts: { [chainId: number]: { [address: Address]: Address } } = {
  [mainnet.id]: {
    [zeroAddress]: '0x5f4eC3Df9cbd43714FE2740f5E3616155c5b8419',
    ['0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48']: '0x8fFfFfd4AfB6115b954Bd326cbe7B4BA576818f6',
  },
  [avalanche.id]: {
    [zeroAddress]: '0x0A77230d17318075983913bC2145DB16C7366156',
    ['0xb97ef9ef8734c71904d8002f8b6bc66dd9c48a6e']: '0xF096872672F44d6EBA71458D74fe67F9a77a23B9', //USDC
    ['0x9702230a8ea53601f5cd2dc00fdbc13d4df4a8c7']: '0xEBE676ee90Fe1112671f19b6B7459bC678B67e8a', //USDT
    ['0x6e84a6216ea6dacc71ee8e6b0a5b7322eebc0fdd']: '0x02D35d3a8aC3e1626d3eE09A78Dd87286F5E8e3a', //JOE
    ['0x420fca0121dc28039145009570975747295f2329']: '0x5B4712ce553E94d9b22bA3CfA10CB6F32fb828E0', //COQ
  },
  [arbitrum.id]: {
    [zeroAddress]: '0x639Fe6ab55C921f74e7fac1ee960C0B6293ba612', //ETH
    ['0x912CE59144191C1204E64559FE8253a0e49E6548']: '0xb2A824043730FE05F3DA2efaFa1CBbe83fa548D6', //ARB
    ['0xf97f4df75117a78c1a5a0dbb814af92458539fb4']: '0x86E53CF1B870786351Da77A57575e79CB55812CB', //LINK
    ['0xaf88d065e77c8cc2239327c5edb3a432268e5831']: '0x50834F3163758fcC1Df9973b6e91f0F0F0434aD3', //USDC
    ['0xfd086bc7cd5c481dcc9c85ebe478a1c0b69fcbb9']: '0x3f3f5dF88dC9F13eac63DF89EC16ef6e7E25DdE7', //USDT
    ['0xda10009cbd5d07dd0cecc66161fc93d7c9000da1']: '0xc5C8E77B397E531B8EC06BFb0048328B30E9eCfB', //DAI
  },
  [base.id]: {
    [zeroAddress]: '0x71041dddad3595F9CEd3DcCFBe3D1F4b0a16Bb70', //ETH
    ['0x833589fcd6edb6e08f4c7c32d4f71b54bda02913']: '0x7e860098F58bBFC8648a4311b374B1D669a2bc6B', //USDC
    ['0xd9aaec86b65d86f6a7b5b1b0c42ffa531710b6ca']: '0x7e860098F58bBFC8648a4311b374B1D669a2bc6B', //USDbC
    ['0xfde4c96c8593536e31f229ea8f37b2ada2699bb2']: '0xf19d560eB8d2ADf07BD6D13ed03e1D11215721F9', //USDT
    ['0x0555e30da8f98308edb960aa94c0db47230d2b9c']: '0xCCADC697c55bbB68dc5bCdf8d3CBe83CdD4E071E', //WBTC
  },
  [bsc.id]: {
    [zeroAddress]: '0x9ef1B8c0E4F7dc8bF5719Ea496883DC6401d5b2e', //ETH
    ['0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d']: '0x51597f405303C4377E36123cBc172b13269EA163', //USDC
  },
}

export const priceStorageAtom = atom<PriceStorage>({})
export const priceSetterAtom = atom(null, (get, set, priceUpdate: PriceResponse) => {
  const prices = get(priceStorageAtom)

  set(priceStorageAtom, {
    ...prices,
    [priceUpdate.chainId]: {
      ...prices[priceUpdate.chainId],
      [priceUpdate.currency.address]: priceUpdate.price,
    },
  })
})

export const parsePrice = (
  price: LatestPriceRPCResponse,
  decimals: Uint8RPCResponse,
  params: PriceRequest,
): PriceResponse => {
  if (price.status === 'success' && decimals.status === 'success') {
    return {
      chainId: params.chainId,
      currency: params.sellCurrency,
      price: formatWeiToAmount(price.result, decimals.result),
    }
  } else {
    throw new RPCCallFailedError(params.sellCurrency.address)
  }
}

export interface RAWCoinGeckoResponse {
  usd: number
}

export interface RAWPhuturePriceResponse {
  phuture: RAWCoinGeckoResponse
}

export const parseQuotePrice = (
  params: PriceRequest,
  response: RAWCoinGeckoResponse,
): PriceResponse => {
  return {
    chainId: params.chainId,
    currency: params.sellCurrency,
    price: Big(response.usd), //may need to change to metadata test this
  }
}

export const parseEPHTRQuotePrice = (params: PriceRequest, response: Big): PriceResponse => {
  return {
    chainId: params.chainId,
    currency: params.sellCurrency,
    price: response, //may need to change to metadata test this
  }
}

const CGPhuturePriceResponse = z.object({ phuture: z.object({ usd: z.number() }) })

export const getPHTRUSDPrice = async (): Promise<RAWCoinGeckoResponse> => {
  const PHTR_PRICE_URL =
    'https://api.coingecko.com/api/v3/simple/price?ids=Phuture&vs_currencies=USD'
  const response = await fetch(PHTR_PRICE_URL)
  const data = CGPhuturePriceResponse.parse(await response.json())

  return data.phuture
}

const CGPriceResponse = z.record(z.object({ usd: z.number() }))

export const getCoinGeckoPrice = async (id: string): Promise<Big> => {
  const COINGECKO_PRICE_URL = `https://api.coingecko.com/api/v3/simple/price?ids=${id}&vs_currencies=USD`
  const res = await fetch(COINGECKO_PRICE_URL)
  const response = CGPriceResponse.parse(await res.json())

  return Big(Number(response[id].usd))
}
